/**
 * Vincent B930 (Eric 2026-02-14 SPEC STRICK PROD) — Bulk import des documents
 * textuels directement dans l'Explorateur (onglet Textes).
 *
 * Auparavant : pour importer un lot de PDF/Word, Vincent devait quitter
 * l'Explorateur, aller sur `/dashboard/upload-text`, importer, revenir.
 * Nouveau : zone drag-and-drop toujours visible en haut de l'onglet Textes.
 * Pipeline complet post-upload (identique à UploadTextPage) :
 *   1. Upload séquentiel (safer que parallèle sur gros PDF)
 *   2. Auto Q&R en background (`/agent/generate-qa-batch/{doc_id}`)
 *   3. Auto-suggestion de tags (`/videos/{doc_id}/tags/suggest`) — pas
 *      d'auto-accept : les tags sont proposés à Vincent qui les valide
 *      via la chip B929 sur la fiche du document.
 *
 * Métadonnées appliquées au batch : Description, Thème, Catégorie, Parcours.
 * Titre : par défaut = nom de fichier sans extension, éditable par ligne.
 *
 * Bloc info « Que se passe-t-il après l'import ? » identique en esprit à
 * la page Import vidéo (UploadPage.js PIPELINE_STEPS).
 */
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, CloudArrowUp, X, SpinnerGap, CheckCircle, ArrowRight, Warning, CaretDown, CaretRight, Globe,
} from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { API_BASE as API } from "../../lib/apiBase";
import { TRAINING_THEMES, themeLabelFor } from "../../lib/trainingThemes";
import { useLang } from "../../contexts/LanguageContext";
import { ACCEPT_DOC, EXTENSIONS_DOC, LISTE_DOC, connaissanceAutorisee } from "../../lib/formatsImport";
import { signalerRefusNavigateur } from "../../lib/refusImport";

// Tâche 1435 — Source unique : voir `lib/formatsImport.js`.
const ACCEPTED = ACCEPT_DOC;
const ACCEPTED_EXT_LIST = EXTENSIONS_DOC;

const STATUS_STYLES = {
  pending: "text-zinc-500 bg-zinc-100",
  uploading: "text-blue-700 bg-blue-100",
  done: "text-teal-700 bg-teal-100",
  error: "text-rose-700 bg-rose-100",
  qa_running: "text-violet-700 bg-violet-100",
  qa_done: "text-teal-700 bg-teal-100",
  // Tâche 1435 — Une source de référence (PNG, URL) ne produit que titre,
  // étiquettes et synthèse : son état doit se DISTINGUER de « Q&R OK », sinon
  // on chercherait des Q&R qui ne viendront jamais.
  reference: "text-sky-700 bg-sky-100",
};

// Étapes visibles dans le bloc « Que se passe-t-il après ? » — identique
// dans l'esprit à PIPELINE_STEPS d'UploadPage.js pour les vidéos.
const PIPELINE_INFO_STEPS = [
  "Upload du fichier vers le stockage sécurisé (Loi 25)",
  `Extraction du texte (${LISTE_DOC})`,
  "Génération automatique des Q&R en arrière-plan",
  "Suggestion automatique d'étiquettes (proposées, à valider)",
  "Fichier prêt à être ouvert, tagué, ajouté à un parcours",
];

export const TextDocsBulkImportZone = ({
  pathways = [],
  categories = [],
  onUploaded,
  defaultOpen = false,
}) => {
  // Tâche 1387 — Les thèmes suivent la langue de l'écran (renverse B629).
  const { lang, tr } = useLang();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const [open, setOpen] = useState(defaultOpen);
  const [items, setItems] = useState([]);
  const [description, setDescription] = useState("");
  const [theme, setTheme] = useState("general");
  const [category, setCategory] = useState("");
  const [selectedPathwayIds, setSelectedPathwayIds] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Tâche 1435, étape 2 — Import par aspiration d'une page web.
  const [url, setUrl] = useState("");
  const [urlOccupe, setUrlOccupe] = useState(false);
  // Repli « coller le texte » (Eric 2026-10-04) pour les pages inaspirables.
  const [collage, setCollage] = useState("");
  const [collageMotif, setCollageMotif] = useState("");

  useEffect(() => {
    const blockDefault = (e) => { e.preventDefault(); };
    window.addEventListener("dragover", blockDefault);
    window.addEventListener("drop", blockDefault);
    return () => {
      window.removeEventListener("dragover", blockDefault);
      window.removeEventListener("drop", blockDefault);
    };
  }, []);

  const addFiles = (filesList) => {
    if (!filesList || filesList.length === 0) return;
    const accepted = [];
    const rejected = [];
    const rejetesBruts = [];
    for (const f of filesList) {
      const ok = ACCEPTED_EXT_LIST.some((ext) => (f.name || "").toLowerCase().endsWith(ext));
      if (ok) {
        accepted.push({
          id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 6)}`,
          file: f,
          title: f.name.replace(/\.[^.]+$/, ""),
          status: "pending",
          doc_id: null,
          error: null,
        });
      } else {
        rejected.push(f.name);
        rejetesBruts.push(f);
      }
    }
    if (rejected.length > 0) {
      toast.warning(`${rejected.length} fichier(s) ignoré(s) — format non supporté : ${rejected.slice(0, 3).join(", ")}`);
      // Tâche 1438 — Ce refus a lieu DANS LE NAVIGATEUR, avant tout envoi :
      // sans cet appel il n'atteindrait jamais le serveur, et la sonde
      // manquerait justement le cas le plus fréquent. Point d'appel UNIQUE,
      // partagé avec la page d'import de textes (`lib/refusImport.js`).
      signalerRefusNavigateur(
        rejetesBruts, `Extension hors de la liste acceptée (${LISTE_DOC})`);
    }
    if (accepted.length > 0) {
      setItems((prev) => [...prev, ...accepted]);
      // Auto-open si on drop pendant que le panneau est fermé.
      if (!open) setOpen(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer?.files || []));
  };

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };
  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));
  const renameItem = (id, newTitle) => updateItem(id, { title: newTitle });

  const triggerQaBatch = async (docId) => {
    try {
      await axios.post(`${API}/api/agent/generate-qa-batch/${docId}?background=1`, {}, { withCredentials: true });
      return true;
    } catch {
      return false;
    }
  };

  const triggerTagSuggest = async (docId) => {
    // B929 — les tags fonctionnent maintenant sur text docs. On les GÉNÈRE
    // seulement (POST /tags/suggest écrit dans `suggested_tags[]`) sans
    // les accepter — Vincent les validera via la chip Étiquettes.
    try {
      await axios.post(`${API}/api/videos/${docId}/tags/suggest`, {}, { withCredentials: true });
      return true;
    } catch {
      return false;
    }
  };

  // Tâche 1435 — La synthèse est l'une des trois SEULES connaissances permises
  // sur une source de référence (PNG, URL). On la lance donc à leur place des
  // Q&R, qui recevraient un refus du serveur (portée 1435).
  const triggerSynthese = async (docId) => {
    try {
      await axios.post(`${API}/api/videos/${docId}/synthesis/generate?background=1`, {}, { withCredentials: true });
      return true;
    } catch {
      return false;
    }
  };

  const triggerConcepts = async (docId) => {
    try {
      await axios.post(`${API}/api/agent/generate-concepts-batch/${docId}?background=1`, {}, { withCredentials: true });
      return true;
    } catch {
      return false;
    }
  };

  const importerUrl = async () => {
    const valeur = url.trim();
    if (!valeur || urlOccupe) return;
    setUrlOccupe(true);
    try {
      const { data } = await axios.post(
        `${API}/api/text-documents/url`,
        {
          url: valeur,
          description: description.trim() || null,
          training_theme: theme,
          category: category || null,
          pathway_ids: selectedPathwayIds.length > 0 ? selectedPathwayIds.join(",") : null,
          texte_colle: collage.trim() || null,
        },
        { withCredentials: true },
      );
      setUrl("");
      setCollage("");
      setCollageMotif("");
      // Les SEULES générations permises depuis une page web (Eric a ajouté les
      // concepts le 2026-10-04 ; Q&R, scripts, procédures et objections
      // restent interdits).
      await Promise.all([
        triggerTagSuggest(data.id), triggerSynthese(data.id), triggerConcepts(data.id),
      ]);
      toast.success(tr(
        `Page importée : « ${data.title} » · synthèse, concepts et étiquettes lancés.`,
        `Page imported: “${data.title}” · summary, concepts and tags launched.`,
      ));
      if (typeof onUploaded === "function") {
        try { await onUploaded(); } catch { /* ignore */ }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Repli choisi par Eric : une page inaspirable (JavaScript, Cloudflare)
      // se colle à la main. Le serveur DIT que c'est possible, l'écran ouvre
      // la zone de collage au lieu de redire « impossible ».
      if (detail && typeof detail === "object" && detail.collage_possible) {
        setCollageMotif(`${detail.message} ${detail.aide || ""}`.trim());
        toast.warning(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail
          : tr("Aspiration impossible.", "Could not fetch the page."));
      }
    } finally {
      setUrlOccupe(false);
    }
  };

  const startBatch = async () => {
    if (items.length === 0 || submitting) return;
    setSubmitting(true);
    let okCount = 0, errCount = 0, qaQueued = 0;
    for (const it of items) {
      if (it.status === "done" || it.status === "qa_done" || it.status === "qa_running" || it.status === "reference") {
        okCount++;
        continue;
      }
      updateItem(it.id, { status: "uploading", error: null });
      try {
        const fd = new FormData();
        fd.append("file", it.file);
        fd.append("title", it.title.trim() || it.file.name);
        if (description.trim()) fd.append("description", description.trim());
        fd.append("training_theme", theme);
        if (category) fd.append("category", category);
        if (selectedPathwayIds.length > 0) fd.append("pathway_ids", selectedPathwayIds.join(","));
        const r = await axios.post(`${API}/api/text-documents/upload`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        const docId = r.data?.id;
        updateItem(it.id, { status: "done", doc_id: docId });
        okCount++;
        // Auto Q&R en background + suggestion tags (en parallèle, non
        // bloquant — les erreurs sont silencieuses côté UX, l'utilisateur
        // pourra relancer manuellement si besoin).
        if (docId) {
          // Tâche 1435 — Une source de référence (PNG) n'autorise que titre,
          // étiquettes et synthèse. Lancer les Q&R ici recevrait un refus du
          // serveur : on afficherait une erreur là où il n'y a pas de faute.
          if (!connaissanceAutorisee(r.data, "qa")) {
            updateItem(it.id, { status: "reference", error: null });
            await Promise.all([triggerTagSuggest(docId), triggerSynthese(docId), triggerConcepts(docId)]);
          } else {
            updateItem(it.id, { status: "qa_running" });
            const [qaOk] = await Promise.all([
              triggerQaBatch(docId),
              triggerTagSuggest(docId),
            ]);
            updateItem(it.id, {
              status: qaOk ? "qa_done" : "done",
              error: qaOk ? null : "Q&R en attente (lancer manuellement)",
            });
            if (qaOk) qaQueued++;
          }
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : (err.message || "Erreur d'import");
        updateItem(it.id, { status: "error", error: msg });
        errCount++;
      }
    }
    setSubmitting(false);
    if (errCount === 0) {
      toast.success(`${okCount} fichier(s) importé(s)${qaQueued > 0 ? ` · Q&R lancée pour ${qaQueued}` : ""}.`);
    } else {
      toast.warning(`${okCount} succès · ${errCount} erreur(s). Voir détails.`);
    }
    // Rafraîchit la liste parent (peu importe le résultat, on a au moins
    // essayé un upload) pour que les fichiers uploadés apparaissent.
    if (typeof onUploaded === "function") {
      try { await onUploaded(); } catch { /* ignore */ }
    }
  };

  const pendingCount = items.filter((i) => i.status === "pending").length;
  const doneCount = items.filter((i) => i.status === "done" || i.status === "qa_done" || i.status === "reference").length;
  const errorCount = items.filter((i) => i.status === "error").length;
  const qaRunningCount = items.filter((i) => i.status === "qa_running").length;

  return (
    <div
      className="mb-4 border border-zinc-200 bg-white rounded-xl overflow-hidden"
      data-testid="text-bulk-import-zone"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors"
        data-testid="text-bulk-import-toggle"
      >
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-800">
          {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
          <CloudArrowUp size={16} className="text-[#002FA7]" weight="duotone" />
          {`Importer des documents (${LISTE_DOC})`}
        </span>
        <span className="text-[11px] text-zinc-500">
          {items.length > 0
            ? `${items.length} fichier(s) · ${doneCount} OK · ${errorCount} erreur(s)`
            : "Glisser-déposer pour ajouter"}
        </span>
      </button>

      {open && (
        <div className="p-4 border-t border-zinc-200 space-y-4">
          {/* Drop zone */}
          <div
            onDragEnter={(e) => { e.preventDefault(); dragCounterRef.current++; setDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); dragCounterRef.current = Math.max(0, dragCounterRef.current - 1); if (dragCounterRef.current === 0) setDragActive(false); }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-colors ${
              dragActive ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-300 hover:border-[#002FA7]/60 bg-white"
            }`}
            data-testid="text-bulk-import-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED}
              multiple
              className="hidden"
              onChange={(e) => { addFiles(Array.from(e.target.files || [])); if (e.target) e.target.value = ""; }}
              data-testid="text-bulk-import-input"
            />
            <CloudArrowUp size={36} weight="duotone" className="text-[#002FA7] mx-auto mb-1" />
            <p className="text-sm font-semibold text-zinc-700">
              Glissez vos PDF/Word ici pour importer en batch
            </p>
            <p className="text-xs text-zinc-500">ou cliquez pour parcourir</p>
            <p className="mt-1 text-[10px] text-zinc-400">
              {`Formats : ${LISTE_DOC} — Max 25 Mo par fichier`}
            </p>
          </div>

          {/* Tâche 1435, étape 2 (Eric 2026-10-02) — Import par ASPIRATION
              d'une page web. Posé sous la zone de dépôt, pas dans un écran
              séparé : c'est le même geste (« ajouter une référence »), et le
              formateur y applique les mêmes réglages de lot (thème,
              catégorie, parcours) que pour un fichier.
              La phrase qui suit n'est pas décorative : une page web ne
              produit QUE titre, étiquettes et synthèse (décision d'Eric), et
              sans le dire on chercherait ses Q&R indéfiniment. */}
          <div className="border border-sky-200 bg-sky-50/60 rounded-xl p-3 space-y-2" data-testid="text-import-url-zone">
            <div className="flex items-center gap-2">
              <Globe size={16} weight="duotone" className="text-sky-600 flex-shrink-0" />
              <span className="text-xs font-bold uppercase tracking-wider text-sky-800">
                {tr("Ou importer une page web", "Or import a web page")}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") importerUrl(); }}
                placeholder="https://exemple.com/page-a-aspirer"
                disabled={urlOccupe}
                className="flex-1 min-w-0 bg-white"
                data-testid="text-import-url-input"
              />
              <Button
                type="button"
                onClick={importerUrl}
                disabled={!url.trim() || urlOccupe}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap"
                data-testid="text-import-url-submit"
              >
                {urlOccupe && <SpinnerGap size={14} className="animate-spin" />}
                {urlOccupe
                  ? tr("Aspiration…", "Fetching…")
                  : tr("Aspirer la page", "Fetch the page")}
              </Button>
            </div>
            <p className="text-[10px] text-sky-900/80 leading-snug">
              {tr(
                "Une page web et une image (.png) ne produisent que le titre, les étiquettes, une synthèse et les concepts — aucune Q&R, procédure, script ni objection. Un bouton « Mettre à jour depuis la page web » reste disponible sur la fiche du document.",
                "A web page and an image (.png) only yield the title, the tags, a summary and the concepts — no Q&A, procedure, script or objection. An “Update from the web page” button stays available on the document page.",
              )}
            </p>

            {/* Tâche 1435 (Eric 2026-10-04) — Repli pour les pages
                inaspirables : une page entièrement construite en JavaScript ou
                protégée par Cloudflare ne rend aucun texte. La zone de collage
                n'apparaît QU'APRÈS un refus, et elle dit pourquoi : l'afficher
                d'emblée inviterait à coller à la main des pages parfaitement
                aspirables. */}
            {collageMotif && (
              <div className="border border-amber-300 bg-amber-50 rounded-lg p-2 space-y-2" data-testid="text-import-url-collage">
                <p className="text-[11px] text-amber-900 leading-snug" data-testid="text-import-url-collage-motif">
                  {collageMotif}
                </p>
                <textarea
                  value={collage}
                  onChange={(e) => setCollage(e.target.value)}
                  rows={5}
                  placeholder={tr(
                    "Collez ici le texte de la page (30 caractères au minimum)…",
                    "Paste the page text here (30 characters minimum)…",
                  )}
                  className="w-full text-xs p-2 border border-amber-300 rounded bg-white outline-none focus:border-[#002FA7]"
                  data-testid="text-import-url-collage-input"
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={importerUrl}
                    disabled={collage.trim().length < 30 || urlOccupe}
                    className="inline-flex items-center gap-2"
                    data-testid="text-import-url-collage-submit"
                  >
                    {urlOccupe && <SpinnerGap size={14} className="animate-spin" />}
                    {tr("Importer le texte collé", "Import the pasted text")}
                  </Button>
                  <button
                    type="button"
                    onClick={() => { setCollageMotif(""); setCollage(""); }}
                    className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
                    data-testid="text-import-url-collage-cancel"
                  >
                    {tr("Annuler", "Cancel")}
                  </button>
                </div>
                <p className="text-[10px] text-amber-800">
                  {tr(
                    "L'adresse et la date sont conservées : la mise à jour restera possible, par un nouveau collage.",
                    "The address and the date are kept: updating stays possible, through a new paste.",
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Liste fichiers */}
          {items.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="text-bulk-import-list">
              <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">
                  {items.length} fichier(s) à importer
                </span>
                <div className="flex items-center gap-3 text-[11px] text-zinc-500">
                  {doneCount > 0 && <span className="inline-flex items-center gap-1 text-teal-600 font-semibold" data-testid="text-bulk-import-count-done"><CheckCircle size={12} weight="fill" />{doneCount}</span>}
                  {qaRunningCount > 0 && <span className="inline-flex items-center gap-1 text-violet-600 font-semibold"><SpinnerGap size={12} className="animate-spin" />{qaRunningCount}</span>}
                  {errorCount > 0 && <span className="inline-flex items-center gap-1 text-rose-600 font-semibold" data-testid="text-bulk-import-count-error"><Warning size={12} weight="fill" />{errorCount}</span>}
                  <button
                    type="button"
                    onClick={() => setItems([])}
                    className="text-[11px] text-zinc-500 hover:text-rose-600 hover:underline"
                    disabled={submitting}
                    data-testid="text-bulk-import-clear-all"
                  >
                    Tout vider
                  </button>
                </div>
              </div>
              <ul className="divide-y divide-zinc-100 max-h-[320px] overflow-y-auto">
                {items.map((it) => (
                  <li
                    key={it.id}
                    className="px-3 py-2 flex items-center gap-3 hover:bg-zinc-50"
                    data-testid={`text-bulk-import-item-${it.id}`}
                  >
                    <FileText size={16} weight="duotone" className="text-[#002FA7] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={it.title}
                        onChange={(e) => renameItem(it.id, e.target.value)}
                        disabled={submitting || it.status !== "pending"}
                        className="w-full bg-transparent border-0 text-sm font-medium text-zinc-900 outline-none focus:bg-white focus:border-b focus:border-[#002FA7] focus:ring-0"
                        placeholder={it.file.name.replace(/\.[^.]+$/, "")}
                        data-testid={`text-bulk-import-item-title-${it.id}`}
                      />
                      <p className="text-[10px] text-zinc-400 mt-0.5">
                        {it.file.name} · {(it.file.size / 1024).toFixed(1)} Ko
                        {it.error && <span className="ml-2 text-rose-600 font-semibold">· {it.error}</span>}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[it.status] || "bg-zinc-100 text-zinc-500"}`}
                      data-testid={`text-bulk-import-item-status-${it.id}`}
                    >
                      {it.status === "pending" && "En attente"}
                      {it.status === "uploading" && (
                        <span className="inline-flex items-center gap-1"><SpinnerGap size={10} className="animate-spin" />Envoi</span>
                      )}
                      {it.status === "done" && "Importé"}
                      {it.status === "qa_running" && (
                        <span className="inline-flex items-center gap-1"><SpinnerGap size={10} className="animate-spin" />Q&R en cours</span>
                      )}
                      {it.status === "qa_done" && "Q&R OK"}
                      {it.status === "reference" && tr("Titre · Tags · Synthèse · Concepts", "Title · Tags · Summary · Concepts")}
                      {it.status === "error" && "Erreur"}
                    </span>
                    {it.doc_id && (
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/videos/${it.doc_id}`)}
                        className="text-xs text-[#002FA7] hover:underline whitespace-nowrap font-semibold"
                        title="Ouvrir le document"
                        data-testid={`text-bulk-import-item-open-${it.id}`}
                      >
                        Ouvrir <ArrowRight size={11} className="inline" />
                      </button>
                    )}
                    {it.status === "pending" && !submitting && (
                      <button
                        type="button"
                        onClick={() => removeItem(it.id)}
                        className="text-zinc-400 hover:text-rose-600"
                        title="Retirer"
                        data-testid={`text-bulk-import-item-remove-${it.id}`}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Metadata form + info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-3 bg-zinc-50 border border-zinc-200 rounded-xl p-4">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold">
                Réglages appliqués à tous les fichiers du batch
              </p>
              <div>
                <Label htmlFor="tbi-desc">Description (optionnel)</Label>
                <Input
                  id="tbi-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Ex. Procédures Onboarding — juillet 2026"
                  data-testid="text-bulk-import-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Thème</Label>
                  <Select value={theme} onValueChange={setTheme}>
                    <SelectTrigger data-testid="text-bulk-import-theme"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TRAINING_THEMES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{themeLabelFor(t, lang)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="tbi-cat">Catégorie (optionnel)</Label>
                  {categories.length > 0 ? (
                    <Select value={category || "__none__"} onValueChange={(v) => setCategory(v === "__none__" ? "" : v)}>
                      <SelectTrigger data-testid="text-bulk-import-category"><SelectValue placeholder="(aucune)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">(aucune)</SelectItem>
                        {categories.map((c) => (
                          <SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="tbi-cat"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      placeholder="Ex. Onboarding clients"
                      data-testid="text-bulk-import-category"
                    />
                  )}
                </div>
              </div>

              {pathways.length > 0 && (
                <div className="space-y-1">
                  <Label>Ajouter aux parcours (optionnel — sélection multiple)</Label>
                  <div className="border border-zinc-200 rounded-lg max-h-40 overflow-y-auto bg-white" data-testid="text-bulk-import-pathways">
                    {["onboarding", "learning", "weekly"].map((ptype) => {
                      const subset = pathways
                        .filter((p) => p.pathway_type === ptype)
                        .slice()
                        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));
                      if (subset.length === 0) return null;
                      return (
                        <div key={ptype} className="border-b border-zinc-100 last:border-0">
                          <div className="px-3 py-1 text-[9px] uppercase text-zinc-500 font-bold tracking-wider bg-zinc-50">
                            {ptype === "onboarding" ? "Parcours d'intégration" : ptype === "weekly" ? "Formations hebdomadaires" : "Parcours d'apprentissage"}
                          </div>
                          {subset.map((p) => (
                            <label
                              key={p.id}
                              className="flex items-center gap-2 px-3 py-1 text-xs hover:bg-zinc-50 cursor-pointer"
                              data-testid={`text-bulk-import-pathway-${p.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedPathwayIds.includes(p.id)}
                                onChange={() =>
                                  setSelectedPathwayIds((prev) =>
                                    prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id]
                                  )
                                }
                                className="w-3.5 h-3.5 accent-[#002FA7]"
                              />
                              <span className="flex-1">{p.name}</span>
                            </label>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <Button
                onClick={startBatch}
                disabled={pendingCount === 0 || submitting}
                className="w-full inline-flex items-center justify-center gap-2"
                data-testid="text-bulk-import-submit"
              >
                {submitting && <SpinnerGap size={16} className="animate-spin" />}
                {submitting
                  ? "Import en cours…"
                  : pendingCount > 0
                    ? `Importer ${pendingCount} fichier(s)`
                    : "Ajoutez des fichiers"}
              </Button>
            </div>

            {/* Bloc info « Que se passe-t-il après ? » — miroir du bloc
                identique sur la page Import vidéo. */}
            <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="text-bulk-import-info">
              <h3 className="text-xs font-bold text-zinc-800 mb-2 uppercase tracking-wider">
                Que se passe-t-il après l'import ?
              </h3>
              <ol className="space-y-2">
                {PIPELINE_INFO_STEPS.map((label, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-zinc-600">
                    <span className="flex-shrink-0 w-5 h-5 rounded-md bg-[#002FA7]/10 text-[#002FA7] font-bold text-[10px] flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="leading-snug">{label}</span>
                  </li>
                ))}
              </ol>
              <p className="mt-3 text-[10px] text-zinc-500 leading-snug">
                Les Q&R apparaîtront sous chaque document en 1-3 min. Les étiquettes suggérées peuvent être acceptées ou rejetées via la chip <strong>Étiquettes</strong> sur la fiche du document.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TextDocsBulkImportZone;
